import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Paper, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { PageShell } from "@/components/common";
import { useLoading } from "@/hooks/useLoading";
import { JobDetails, JobService } from "@/services/features/JobService";

import JsonReportViewer, { hasJsonReport } from "./JsonReportViewer";

const isValidJobId = (jobId: string | undefined): jobId is string =>
  !!jobId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(jobId);

const ETLRunReport: React.FC = () => {
  const { t } = useTranslation();
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = useLoading();

  const [job, setJob] = useState<JobDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  const jobService = JobService.getInstance();

  useEffect(() => {
    if (!isValidJobId(jobId)) {
      enqueueSnackbar(t("operations.etl.eTLRunDetails.notifications.invalidJobId"), {
        variant: "error",
      });
      navigate("/admin/data/etl-runs");
      return;
    }

    document.title = t("operations.etl.eTLRunReport.documentTitle");
    fetchJobReport();
  }, [jobId, navigate, t]);

  const fetchJobReport = async () => {
    if (!isValidJobId(jobId)) {
      setError(t("operations.etl.eTLRunDetails.errors.invalidJobId"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await jobService.getJobDetails(jobId);

      if (response.success && response.data) {
        setJob(response.data);
        document.title = t("operations.etl.eTLRunReport.documentTitleWithJobName", {
          jobName: response.data.job.name,
        });
      } else {
        throw new Error(
          response.error || t("operations.etl.eTLRunDetails.errors.failedFetchJobDetails"),
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("operations.etl.eTLRunDetails.errors.errorOccurred");
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const backToRunButton = (
    <Button
      component={RouterLink}
      to={isValidJobId(jobId) ? `/admin/data/etl-runs/${jobId}` : "/admin/data/etl-runs"}
      startIcon={<ArrowBackIcon />}
      variant="outlined"
    >
      {t("operations.etl.eTLRunReport.actions.backToRun")}
    </Button>
  );

  if (error) {
    return (
      <PageShell
        breadcrumbs="admin"
        title={t("operations.etl.eTLRunReport.title")}
        headerActions={backToRunButton}
      >
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </PageShell>
    );
  }

  if (!job) {
    return (
      <PageShell breadcrumbs="admin" title={t("operations.etl.eTLRunReport.title")}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  const hasReport = hasJsonReport(job.job.report);

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("operations.etl.eTLRunReport.title")}
      headerActions={
        <Stack direction="row" spacing={2}>
          {backToRunButton}
        </Stack>
      }
    >
      {!hasReport ? (
        <Alert severity="info">{t("operations.etl.eTLRunReport.empty.noReport")}</Alert>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
          }}
        >
          <JsonReportViewer report={job.job.report} maxHeight="calc(100vh - 220px)" />
        </Paper>
      )}
    </PageShell>
  );
};

export default ETLRunReport;
